import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { 
  EyeIcon, 
  EyeSlashIcon,
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

interface AcceptForm {
  name: string
  password: string
  confirmPassword: string
}

interface InvitationData {
  organizationName: string
  inviterName: string
  role: string
  email: string
  expiresAt: string
}

export default function AcceptInvitation() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { acceptInvitation, login } = useAuth()
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptForm>()

  const password = watch('password')

  const onSubmit = async (data: AcceptForm) => {
    setIsLoading(true)
    try {
      // Get token from URL params
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      
      if (!token) {
        throw new Error('Invalid invitation link')
      }

      const response = await acceptInvitation(token, {
        name: data.name,
        password: data.password
      })
      
      // Automatically log in the user after successful invitation acceptance
      try {
        await login(invitation?.email || '', data.password)
        toast.success('Account created and logged in successfully!')
        window.location.href = '/dashboard'
      } catch (loginError) {
        // If auto-login fails, redirect to login page
        toast.success('Account created successfully! Please log in to continue.')
        window.location.href = '/login'
      }
    } catch (error) {
      // Error is handled in AuthContext
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    const loadInvitationData = async () => {
      try {
        // Get token from URL params
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        
        if (!token) {
          setError('Invalid invitation link')
          setLoading(false)
          return
        }

        // Fetch invitation data from API using token
        const response = await fetch(`/api/auth/invitations/${token}/verify`)
        const data = await response.json()
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load invitation details')
        }
        
        // The API returns the data directly, not wrapped in a data property
        setInvitation(data)
      } catch (error: any) {
        setError('Failed to load invitation details')
      } finally {
        setLoading(false)
      }
    }

    loadInvitationData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-card">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-card py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <h1 className="text-page-title text-text-dark mb-4">Invalid Invitation</h1>
          <p className="text-body text-text-muted">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-card py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/cmt-logo.png" 
              alt="Connect Management Logo" 
              className="h-32 w-auto"
            />
          </div>
          <h2 className="text-center text-page-title text-text-dark">
            You're Invited!
          </h2>
        </div>

        {/* Invitation Details Section */}
        <div className="card bg-background-card border border-border-light">
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-accent-blue/10 rounded-full mx-auto mb-4">
                <BuildingOfficeIcon className="w-8 h-8 text-accent-blue" />
              </div>
              <h3 className="text-card-title text-text-dark">{invitation?.organizationName}</h3>
              <p className="text-small text-text-muted mt-1">
                {invitation?.inviterName} has invited you to join as a {invitation?.role}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-center mb-4">
              <div className="p-3 bg-background-card rounded-default border border-border-light">
                <div className="flex items-center justify-center mb-2">
                  <EnvelopeIcon className="w-5 h-5 text-text-muted" />
                </div>
                <p className="text-caption text-text-muted">Email</p>
                <p className="text-small text-text-dark font-medium break-words">{invitation?.email}</p>
              </div>
              
              <div className="p-3 bg-background-card rounded-default border border-border-light">
                <div className="flex items-center justify-center mb-2">
                  <UserIcon className="w-5 h-5 text-text-muted" />
                </div>
                <p className="text-caption text-text-muted">Role</p>
                <p className="text-small text-text-dark font-medium">{invitation?.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Password Creation Section */}
        <div className="card bg-background-card border border-border-light">
          <div className="p-6">
            <h3 className="text-section-title text-text-dark mb-4 text-center">
              Create Your Account
            </h3>
            <p className="text-body text-text-muted mb-6 text-center">
              Set up your password to complete the invitation
            </p>
            
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="name" className="block text-small text-text-dark font-medium mb-2">
                  Your Name
                </label>
                <input
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 2,
                      message: 'Name must be at least 2 characters',
                    },
                  })}
                  type="text"
                  autoComplete="name"
                  className="input-field"
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="mt-1 text-caption text-error">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-small text-text-dark font-medium mb-2">
                  Create Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-text-muted" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-text-muted" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-caption text-error">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-small text-text-dark font-medium mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === password || 'Passwords do not match',
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pr-10"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-text-muted" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-text-muted" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-caption text-error">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Accepting invitation...' : 'Accept Invitation'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
} 